import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Icon, type IconName } from "../components/Icon";
import { Logo } from "../components/Logo";
import { useI18n, type MessageKey } from "../lib/i18n";
import { useFeatures, useMe, useNotifications } from "../lib/queries";
import { Avatar } from "../components/ui";

/** Desktop sidebar only: places that don't need a tab on a phone. */
const secondary: { to: string; label: MessageKey; icon: IconName }[] = [
  { to: "/app/activity", label: "nav.activity", icon: "activity" },
  { to: "/app/notifications", label: "notif.title", icon: "bell" },
  { to: "/app/security", label: "security.title", icon: "shield" },
  { to: "/developers", label: "landing.developers", icon: "code" },
];

/** Phone tabs (Pay in the middle). On desktop the sidebar adds Circles and Community. */
const tabs: { to: string; label: MessageKey; icon: IconName; end?: boolean; pay?: boolean; desktopOnly?: boolean }[] = [
  { to: "/app", label: "nav.home", icon: "home", end: true },
  { to: "/app/plan", label: "nav.plan", icon: "chart" },
  { to: "/app/receipts", label: "subnav.receipts", icon: "activity", desktopOnly: true },
  { to: "/app/insights", label: "subnav.insights", icon: "globe", desktopOnly: true },
  { to: "/app/pay", label: "nav.pay", icon: "pay", pay: true },
  { to: "/app/card", label: "nav.card", icon: "card" },
  { to: "/app/circles", label: "nav.circles", icon: "circle", desktopOnly: true },
  { to: "/app/community", label: "nav.community", icon: "heart", desktopOnly: true },
  { to: "/app/me", label: "nav.me", icon: "user" },
];

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

function Bell() {
  const { t } = useI18n();
  const n = useNotifications();
  const unread = n.data?.unread ?? 0;
  return (
    <Link to="/app/notifications" className="bell" aria-label={unread ? t("notif.unread", { n: unread }) : t("notif.title")}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      {unread > 0 && <span className="bell-dot">{unread > 9 ? "9+" : unread}</span>}
    </Link>
  );
}

/**
 * Which licensed-level feature a screen belongs to. Those screens carry the "test money" banner
 * until the production guard allows real money for that feature. Screens of N0 features (money
 * plan, My finances, receipts, proof of funds, coordination circles) handle no money and don't.
 */
export function testMoneyFeatureFor(pathname: string): string | null {
  if (/^\/app\/(plan|finances|receipts|insights|proof|me|security|notifications|admin)(\/|$)/.test(pathname)) return null;
  if (/^\/app\/circles\/[^/]+/.test(pathname) && !/^\/app\/circles\/new/.test(pathname)) return null; // the circle page decides by its mode
  if (/^\/app\/circles/.test(pathname)) return "tontine_cagnotte";
  if (/^\/app\/card/.test(pathname)) return "programme_cartes";
  if (/^\/app(\/|$)/.test(pathname)) return "portefeuille_multidevise";
  if (/^\/(checkout|pay|c)\//.test(pathname)) return "portefeuille_multidevise";
  return null;
}

/** The "test money" banner. With a feature, it shows only while that feature isn't on real money. */
export function BetaBar({ feature }: { feature?: string | null }) {
  const { t } = useI18n();
  const flags = useFeatures();
  if (feature === null) return null;
  if (feature && flags.data?.flags.find((f) => f.id === feature)?.realMoney) return null;
  return <div className="beta-bar">{t("beta.banner")}</div>;
}

export function AppShell() {
  const { t } = useI18n();
  const me = useMe().data;
  const online = useOnline();
  const { pathname } = useLocation();
  // Auto-update: the new version activates as soon as it is deployed. Also check hourly
  // for long-lived sessions (an app left open on a phone).
  useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => registration.update().catch(() => {}), 60 * 60 * 1000);
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="app-shell">
      <BetaBar feature={testMoneyFeatureFor(pathname)} />
      <div className="app-top">
        <Link to="/app" className="brand-link" style={{ textDecoration: "none" }} aria-label="CafonaPay home">
          <Logo size={28} />
        </Link>
        <div className="app-top-actions">
          <Bell />
          {me && (
            <Link to="/app/me" className="top-avatar" aria-label={t("nav.me")}>
              <Avatar name={me.name} />
            </Link>
          )}
        </div>
      </div>
      <main className="app-main">
        {!online && <div className="notice">{t("common.offline")}</div>}
        <Outlet />
      </main>
      <nav className="tabbar" aria-label="Main">
        <Link to="/app" className="tabbar-brand" aria-label="CafonaPay home">
          <Logo size={26} />
        </Link>
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className={[tab.pay ? "pay-tab" : "", tab.desktopOnly ? "desktop-only" : ""].join(" ").trim() || undefined}>
            <span>
              <Icon name={tab.icon} />
            </span>
            <span>{t(tab.label)}</span>
          </NavLink>
        ))}
        <div className="tabbar-secondary">
          {secondary.map((item) => (
            <NavLink key={item.to} to={item.to}>
              <span>
                <Icon name={item.icon} />
              </span>
              <span>{t(item.label)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
